import { useAction, useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { AsyncStateN, Array } from '@blade/std-lib'
import { useTranslation } from '@root/shared/localization'
import { InfoMessage } from '@root/shared/ui-kit'
import { FadeOverflowVirtuozo } from '@root/shared/ui-kit/utils/fade-overflow-virtuozo'

import { RelationFollowing } from './relation-following'

import type { SearchUser } from '@root/entities/search-user'
import type { UsersSearchModel } from '@root/features/user/user-connections/atoms/reatom-users-search'

type SearchModalResultsProps = {
  model: UsersSearchModel
}

export const GlobalSearchUsersResults: React.FC<SearchModalResultsProps> = ({
  model,
}) => {
  const { t } = useTranslation()
  const [searchQuery] = useAtom(model.searchQueryAtom)
  const [searchState] = useAtom(model.paginatedModel)
  const refresh = useAction(model.paginatedModel.refresh)
  const loadMore = useAction(model.paginatedModel.loadMore)
  const [loadMoreState] = useAtom(model.paginatedModel.loadMoreState)
  const [hasMore] = useAtom(model.paginatedModel.hasMore)

  return AsyncStateN.match(searchState, {
    NotInitiated: () => (
      <InfoWrapper>
        <InfoMessage intent={'empty'} variant="dt" title={t('common.search')} />
      </InfoWrapper>
    ),
    Loading: () => (
      <ListWrapper>
        {Array.range(0, 5).map((x) => (
          <RelationFollowing.Skeleton key={x} />
        ))}
      </ListWrapper>
    ),
    Error: () => (
      <InfoWrapper>
        <InfoMessage intent={'error'} variant="dt" onRetry={refresh} />
      </InfoWrapper>
    ),
    Success: (response) =>
      Array.match(response.data, {
        onEmpty: () => (
          <InfoWrapper>
            <InfoMessage
              intent={'empty'}
              variant="dt"
              title={t('common.no-results-found-for', { query: searchQuery })}
              description={t('common.try-changing-search-query')}
            />
          </InfoWrapper>
        ),
        onNonEmpty: (nonEmptyUsers) => (
          <List
            data={nonEmptyUsers}
            endReached={loadMore}
            itemContent={(index, user) => (
              <Item key={user.delugeId}>
                <RelationFollowing
                  follower={{ ...user, deployedTokensCount: undefined }}
                />
              </Item>
            )}
            components={{
              Footer: () => {
                if (!hasMore) {
                  return undefined
                }
                return AsyncStateN.matchWaiting(loadMoreState, {
                  Error: () => (
                    <InfoMessage
                      intent={'error'}
                      variant="dt"
                      onRetry={loadMore}
                    />
                  ),
                  Waiting: () => (
                    <>
                      {Array.range(0, 2).map((x) => (
                        <RelationFollowing.Skeleton key={x} />
                      ))}
                    </>
                  ),
                  Success: () => undefined,
                })
              },
            }}
          />
        ),
      }),
  })
}

const List = styled(FadeOverflowVirtuozo<SearchUser.SearchUser, never>)`
  height: 100%;
`

const ListWrapper = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column;

  min-height: 0;
  overflow: hidden;
  gap: 6px;
  padding-top: 8px;
`
const InfoWrapper = styled.div`
  display: grid;
  height: 100%;
`

const Item = styled.div`
  margin: 0 0 6px;
`
